/**
 * Mobile Team Stats - SLOT-BASED LAYOUT (Matches Desktop)
 * Uses position slots from league settings, assigns players to slots
 */

import React, { useState, useEffect, useMemo } from 'react';
import { dynastyTheme, dynastyTileStyle } from '../../../services/colorService';
import MobileTableSectionHeader from '../components/MobileTableSectionHeader';
import { leaguesAPI } from '../../../services/apiService';
import { MobileDynastyTable } from '../components/MobileDynastyTable';
import { createMobileBattersColumns, createMobilePitchersColumns } from '../components/mobileTeamStatsColumns';
import { getStatConfigs, DEFAULT_BATTING_STATS, DEFAULT_PITCHING_STATS } from '../../../utils/statMapping';
import { computeTotals } from '../../../services/tables/statTotals';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';
import { useCommissioner } from '../../../contexts/CommissionerContext';
import CommissionerModeBar from '../../commissioner/CommissionerModeBar';
import { classifyPlayerSection } from '../../../utils/playerClassification';

const MobileTeamStats = ({ leagueId, selectedTeamId: propSelectedTeamId }) => {
  const { 
    isCommissionerMode, 
    activeTeamId,
    isCommissioner,
    enterCommissionerMode
  } = useCommissioner();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [players, setPlayers] = useState([]);
  const [league, setLeague] = useState(null);
  const [activeTab, setActiveTab] = useState('hitting');
  const [accruedOnlyMode, setAccruedOnlyMode] = useState(true);
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [userTeamId, setUserTeamId] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);
  
  // Modal/Card state
  const [showModal, setShowModal] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  useEffect(() => {
    if (leagueId) {
      loadAllTeams();
    }
  }, [leagueId]);

  // Use prop selectedTeamId if provided (from parent), otherwise use local state
  const effectiveSelectedTeamId = propSelectedTeamId || selectedTeamId;

  useEffect(() => {
    if (leagueId && (effectiveSelectedTeamId || userTeamId || isCommissionerMode)) {
      loadData();
    }
  }, [leagueId, effectiveSelectedTeamId, userTeamId, isCommissionerMode, activeTeamId]);

  // Update teamInfo when selected team or commissioner mode changes
  useEffect(() => {
    if (allTeams.length === 0) return;
    
    let team = null;
    if (isCommissionerMode && activeTeamId) {
      team = allTeams.find(t => t.team_id === activeTeamId);
    } else if (effectiveSelectedTeamId) {
      team = allTeams.find(t => t.team_id === effectiveSelectedTeamId);
    } else {
      team = allTeams.find(t => t.is_user_team);
    }
    
    if (team) {
      setTeamInfo(team);
    }
  }, [effectiveSelectedTeamId, activeTeamId, isCommissionerMode, allTeams]);

  const loadAllTeams = async () => {
    try {
      if (!leagueId) return;
      
      const response = await leaguesAPI.getLeagueTeams(leagueId);
      
      if (response.success && response.teams) {
        const teamList = response.teams.map(team => ({
          team_id: team.team_id,
          team_name: team.team_name || 'Unnamed Team',
          manager_name: team.manager_name || 'Unknown Manager',
          is_user_team: team.is_user_team || false,
          team_logo_url: team.team_logo_url || null,
          total_points: team.total_points || 0,
          rank: team.rank || null,
          total_teams: response.teams.length
        }));
        
        setAllTeams(teamList);
        
        const userTeam = teamList.find(t => t.is_user_team);
        if (userTeam) {
          setUserTeamId(userTeam.team_id);
          if (!selectedTeamId && !isCommissionerMode) {
            setSelectedTeamId(userTeam.team_id);
            setTeamInfo(userTeam);
          }
        }
      }
    } catch (err) {
      console.error('Error loading teams:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Determine which team to load
      const targetTeamId = isCommissionerMode ? activeTeamId : (effectiveSelectedTeamId || userTeamId);
      
      // Fetch both league details and roster
      const [leagueResponse, rosterResponse] = await Promise.all([
        leaguesAPI.getLeagueDetails(leagueId),
        targetTeamId && targetTeamId !== userTeamId
          ? leaguesAPI.getTeamRosterCanonical(leagueId, targetTeamId)
          : leaguesAPI.getMyRosterCanonical(leagueId)
      ]);
      
      if (leagueResponse && leagueResponse.success) {
        setLeague(leagueResponse.league);
      }
      
      if (rosterResponse && rosterResponse.success) {
        console.log('Loaded roster:', rosterResponse.players?.length, 'players');
        setPlayers(rosterResponse.players || []);
      }
    } catch (err) {
      console.error('Error loading team stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamChange = (e) => {
    const newTeamId = e.target.value;
    setSelectedTeamId(newTeamId);
    const selectedTeam = allTeams.find(t => t.team_id === newTeamId);
    if (selectedTeam) {
      setTeamInfo(selectedTeam);
    }
    // Reload data for the new team
    loadData();
  };

  const getCurrentTeam = () => {
    if (isCommissionerMode && activeTeamId) {
      return allTeams.find(t => t.team_id === activeTeamId) || teamInfo;
    }
    if (effectiveSelectedTeamId) {
      return allTeams.find(t => t.team_id === effectiveSelectedTeamId) || teamInfo;
    }
    return allTeams.find(t => t.is_user_team) || teamInfo;
  };

  const currentTeam = getCurrentTeam();
  
  // Player click handlers
  // MobilePlayerLink hands us the MLB id (canonical contract); store it in the
  // shape the modal guard/render reads (selectedPlayer.ids.mlb), matching MobileRoster.
  const handlePlayerClick = (mlbId) => {
    if (!mlbId) return;
    setSelectedPlayer({ ids: { mlb: mlbId } });
    setShowModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setShowCard(false);
    setSelectedPlayer(null);
  };
  
  const handleOpenCard = () => {
    setShowModal(false);
    setShowCard(true);
  };

  // Get league positions from position_requirements (MATCHES DESKTOP)
  const getLeaguePositions = () => {
    const positions = {
      hitting: [],
      pitching: []
    };

    if (league?.roster_settings?.position_requirements && typeof league.roster_settings.position_requirements === 'object') {
      const posReqs = league.roster_settings.position_requirements;
      
      Object.entries(posReqs).forEach(([positionKey, config]) => {
        const slots = config?.slots || 0;
        
        if (positionKey === 'P' && slots > 0) {
          positions.pitching.push(...Array(slots).fill('P'));
        } else if (slots > 0 && !['BN', 'DL', 'MIN'].includes(positionKey)) {
          // Don't create slots for bench/DL/minors
          positions.hitting.push(...Array(slots).fill(positionKey));
        }
      });
    } else {
      // Fallback defaults
      positions.hitting = ['C', '1B', '2B', 'SS', '3B', 'OF', 'OF', 'OF', 'UTIL'];
      positions.pitching = Array(10).fill('P');
    }
    
    return positions;
  };

  const positions = getLeaguePositions();

  // ========================================
  // DYNAMIC STAT CATEGORIES FROM LEAGUE
  // ========================================
  const statCategories = useMemo(() => {
    if (league?.league_settings?.scoring_categories) {
      return {
        hitting: league.league_settings.scoring_categories.hitting || DEFAULT_BATTING_STATS,
        pitching: league.league_settings.scoring_categories.pitching || DEFAULT_PITCHING_STATS
      };
    }
    
    return {
      hitting: DEFAULT_BATTING_STATS,
      pitching: DEFAULT_PITCHING_STATS
    };
  }, [league]);

  // Get stat configs for current tab
  const currentStatConfigs = useMemo(() => {
    const statLabels = activeTab === 'hitting' ? statCategories.hitting : statCategories.pitching;
    const isPitcher = activeTab === 'pitching';
    return getStatConfigs(statLabels, isPitcher);
  }, [activeTab, statCategories]);

  // Filter players by type and status - USE RAW CANONICAL DATA (no transforms)
  const filteredPlayers = useMemo(() => {
    // Canonical section classification (Bible §4.5.9.1): active-slot players
    // by slot (TWP at P_6 → pitching), non-active by primary position.
    const hasAccrued = (p) =>
      p.stats?.active_accrued?.hitting || p.stats?.active_accrued?.pitching;
    return {
      hitting: {
        active: players.filter(p =>
          classifyPlayerSection(p) === 'hitting' &&
          p.league_context?.roster?.status === 'active'),
        nonActive: players.filter(p =>
          classifyPlayerSection(p) === 'hitting' &&
          p.league_context?.roster?.status !== 'active' && hasAccrued(p))
      },
      pitching: {
        active: players.filter(p =>
          classifyPlayerSection(p) === 'pitching' &&
          p.league_context?.roster?.status === 'active'),
        nonActive: players.filter(p =>
          classifyPlayerSection(p) === 'pitching' &&
          p.league_context?.roster?.status !== 'active' && hasAccrued(p))
      }
    };
  }, [players]);

  // Get raw canonical data for current tab (DynastyTable handles everything)
  const category = activeTab;
  const activeLineupPlayers = filteredPlayers[category].active;
  const nonActivePlayers = filteredPlayers[category].nonActive;

  // Create columns using mobile column creators.
  // Bible § 4 stat-column rule: pass currentStatConfigs (derived from
  // league_settings.scoring_categories) so dynamic stat columns are league-
  // accurate. Static context columns (G/AB for batters, G/GS for pitchers)
  // and financial columns are declared inside the factory.
  const activeLineupColumns = useMemo(() => {
    if (activeTab === 'pitching') {
      return createMobilePitchersColumns({
        onPlayerClick: handlePlayerClick,
        statConfigs: currentStatConfigs,
      });
    }
    return createMobileBattersColumns({
      onPlayerClick: handlePlayerClick,
      statConfigs: currentStatConfigs,
    });
  }, [activeTab, currentStatConfigs]);

  const nonActiveColumns = useMemo(() => {
    if (activeTab === 'pitching') {
      return createMobilePitchersColumns({
        onPlayerClick: handlePlayerClick,
        excludePlayerColumn: false,  // Keep player column for Other Players table
        statConfigs: currentStatConfigs,
      });
    }
    return createMobileBattersColumns({
      onPlayerClick: handlePlayerClick,
      excludePlayerColumn: false,
      statConfigs: currentStatConfigs,
    });
  }, [activeTab, currentStatConfigs]);

  // Team Totals columns - exclude player, team, and financial columns, hide POS header
  const teamTotalsColumns = useMemo(() => {
    const columns = activeTab === 'pitching'
      ? createMobilePitchersColumns({
          onPlayerClick: handlePlayerClick,
          excludePlayerColumn: true,      // Remove Player column from Team Totals
          excludeTeamColumn: true,        // Remove Team column from Team Totals
          excludeFinancialColumns: true,  // Remove Price, Salary, Contract columns
          statConfigs: currentStatConfigs,
        })
      : createMobileBattersColumns({
          onPlayerClick: handlePlayerClick,
          excludePlayerColumn: true,
          excludeTeamColumn: true,
          excludeFinancialColumns: true,
          statConfigs: currentStatConfigs,
        });
    
    // Hide POS header for totals table
    return columns.map(col => {
      if (col.key === 'position_slot') {
        return { ...col, header: '' };
      }
      return col;
    });
  }, [activeTab, currentStatConfigs]);

  if (loading) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        color: dynastyTheme.tokens.colors.neutral[400]
      }}>
        <div style={{
          width: '30px',
          height: '30px',
          border: `3px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
          borderTop: `3px solid ${dynastyTheme.tokens.colors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 10px'
        }}></div>
        Loading stats...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        backgroundColor: dynastyTheme.tokens.colors.error + '20',
        border: `1px solid ${dynastyTheme.tokens.colors.error}`,
        borderRadius: '8px',
        color: dynastyTheme.tokens.colors.error
      }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      {/* Commissioner Mode Bar - Compact */}
      {isCommissionerMode && league && (
        <div style={{ padding: '0 20px', marginBottom: '10px' }}>
          <CommissionerModeBar 
            league={league}
            onTeamSwitch={() => {
              const switchedTeam = allTeams.find(t => t.team_id === activeTeamId);
              if (switchedTeam) {
                setTeamInfo(switchedTeam);
              }
              loadData();
            }}
          />
        </div>
      )}

      {/* ACTIVE — flush 3px-bar header; Hitters/Pitchers toggle INLINE right
          (replaces the old centered band) + Totals/3-Line action (no card). */}
      <div>
        <MobileTableSectionHeader
          label="Accrued · Active"
          color={dynastyTheme.tokens.colors.primary}
          tabs={{
            active: activeTab,
            onSelect: setActiveTab,
            options: [
              { key: 'hitting', label: `Hitters ${positions.hitting.length}` },
              { key: 'pitching', label: `Pitchers ${positions.pitching.length}` },
            ],
          }}
          action={(
            <button
              onClick={() => setAccruedOnlyMode(!accruedOnlyMode)}
              style={{
                padding: '2px 5px',
                background: 'none',
                border: `1px solid ${accruedOnlyMode ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[700]}`,
                borderRadius: '4px',
                color: accruedOnlyMode ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[500],
                fontWeight: '600',
                fontSize: '9px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {accruedOnlyMode ? 'Totals' : '3-Line'}
            </button>
          )}
        />

        <MobileDynastyTable
          tableType="roster-lineup"
          leaguePositions={positions}
          playerType={activeTab}
          rowsPerItem={accruedOnlyMode ? 1 : 3}
          rowTypes={accruedOnlyMode ? ['accrued'] : ['season', 'rolling', 'accrued']}
          data={activeLineupPlayers}
          columns={activeLineupColumns}
          stickyHeader={false}
          enableHorizontalScroll={true}
          enableVerticalScroll={false}
          maxHeight="none"
          minWidth="550px"
        />
      </div>
      
      {/* OTHER PLAYERS TABLE (bench, DL, minors with accrued stats) */}
      {nonActivePlayers.length > 0 && (
        <div>
          <MobileTableSectionHeader label="Accrued · Inactive" color={dynastyTheme.tokens.colors.primary} count={nonActivePlayers.length} />

          <MobileDynastyTable
            tableType="player-list"
            rowsPerItem={accruedOnlyMode ? 1 : 3}
            rowTypes={accruedOnlyMode ? ['accrued'] : ['season', 'rolling', 'accrued']}
            data={nonActivePlayers}
            columns={nonActiveColumns}
            stickyHeader={false}
            enableHorizontalScroll={true}
            enableVerticalScroll={false}
            maxHeight="none"
            minWidth="550px"
          />
        </div>
      )}
      
      {/* TEAM TOTALS TABLE - ONLY IN ACCRUED MODE */}
      {accruedOnlyMode && (
      <div>
        <MobileTableSectionHeader label="Team Totals" color={dynastyTheme.tokens.colors.primary} />
        <p style={{
          fontSize: '11px',
          color: dynastyTheme.tokens.colors.neutral[400],
          margin: '0 4px 10px'
        }}>
          Aggregate totals for ALL players (active + non-active with accrued stats)
        </p>

        <MobileDynastyTable
          tableType="player-list"
          rowsPerItem={1}
          rowTypes={['accrued']}
          data={[]}
          columns={teamTotalsColumns}
          stickyHeader={false}
          enableHorizontalScroll={true}
          enableVerticalScroll={false}
          maxHeight="none"
          minWidth="550px"
          showTotals={true}
          totalsRow={{
            isTotalsRow: true,
            // DD-STAT-CONSOLIDATION C1: totals now come from the SSoT engine — SLG from
            // real total bases (incl 2B/3B), OBP incl HBP/SF, OPS/ERA/WHIP from summed
            // components. Replaces the prior inline (hits + HR*3)/AB SLG that understated
            // SLG & OPS, and the OBP that ignored HBP/SF.
            totals: computeTotals(
              [...activeLineupPlayers, ...nonActivePlayers],
              { statType: activeTab === 'pitching' ? 'pitching' : 'hitting', window: 'active_accrued' },
            ),
          }}
        />  
      </div>
      )}
      
      {/* Player Modal & Card */}
      {showModal && selectedPlayer && selectedPlayer.ids?.mlb && (
        <MobilePlayerModal
          playerId={selectedPlayer.ids.mlb}
          isOpen={showModal}
          onClose={handleCloseModal}
          onExpandToCard={handleOpenCard}
          leagueId={leagueId}
        />
      )}
      
      {showCard && selectedPlayer && (
        <MobilePlayerCard
          player={selectedPlayer}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default MobileTeamStats;
